import { useCallback, useEffect, useState } from "react";
import { hivesApi } from "../../services/hivesApi";
import { useToast } from "../../context/ToastContext";
import { Card } from "../common/Card";
import { Button } from "../common/Button";
import { Badge } from "../common/Badge";
import { Modal } from "../common/Modal";
import { Field } from "../common/Field";
import { formatDateOnly } from "../../services/hiveActions";

/**
 * What is wrong with this hive, and what was wrong before (#251).
 *
 * Reported as "issues cannot be resolved, and cannot be told apart".
 * Both halves were true: "Log Issue" wrote an action with no details
 * whatsoever, so every issue was identical to every other one, and there
 * was nowhere for "I fixed it" to go, so the list could only grow.
 *
 * Open ones lead, urgent first. Resolved ones stay visible underneath
 * because "has this happened before" is a real question about a hive that
 * keeps giving trouble.
 */
export function IssuePanel({ hiveId, onChanged }) {
  const { notify } = useToast();
  const [status, setStatus] = useState("loading");
  const [issues, setIssues] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState({ summary: "", severity: "Watch", notes: "" });
  const [resolving, setResolving] = useState(null);
  const [resolution, setResolution] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const r = await hivesApi.listIssues(hiveId);
      setIssues(r.issues || []);
      setStatus("ready");
    } catch {
      // A real HTTP error is the server's answer and has to surface
      // (#166/#217). A panel that silently shows nothing wrong is worse
      // than one that admits it could not find out.
      setStatus("error");
    }
  }, [hiveId]);

  useEffect(() => { load(); }, [load]);

  async function submit() {
    if (!form.summary.trim()) return notify("Say what's wrong — that's the whole point", "warning");
    setSaving(true);
    try {
      await hivesApi.openIssue(hiveId, {
        summary: form.summary.trim(),
        severity: form.severity,
        notes: form.notes.trim() || undefined,
      });
      notify("Issue logged", "success");
      setOpenForm(false);
      setForm({ summary: "", severity: "Watch", notes: "" });
      load();
      // The hive timeline gains an IssueFound entry, and the dashboard
      // count changes, so the page above has to refetch too.
      onChanged?.();
    } catch (err) {
      notify(err.apiMessage || "Could not log the issue", "danger");
    } finally {
      setSaving(false);
    }
  }

  async function confirmResolve() {
    setSaving(true);
    try {
      await hivesApi.resolveIssue(resolving.issue_id, resolution.trim() || undefined);
      notify("Issue resolved", "success");
      setResolving(null);
      setResolution("");
      load();
      onChanged?.();
    } catch (err) {
      notify(err.apiMessage || "Could not resolve the issue", "danger");
    } finally {
      setSaving(false);
    }
  }

  async function reopen(issue) {
    try {
      await hivesApi.reopenIssue(issue.issue_id);
      notify("Issue reopened", "success");
      load();
      onChanged?.();
    } catch (err) {
      notify(err.apiMessage || "Could not reopen the issue", "danger");
    }
  }

  const open = issues.filter((i) => !i.resolved_date);
  const resolved = issues.filter((i) => i.resolved_date);

  return (
    <Card
      title="Issues"
      className="section-spacing"
      actions={<Button variant="secondary" size="sm" onClick={() => setOpenForm(true)}>Log Issue</Button>}
    >
      {status === "loading" && <p style={{ margin: 0, color: "var(--color-text-muted)" }}>Loading…</p>}
      {status === "error" && (
        <div>
          <p style={{ marginTop: 0 }}>Couldn’t load this hive’s issues.</p>
          <Button variant="secondary" onClick={load}>Try again</Button>
        </div>
      )}

      {status === "ready" && open.length === 0 && (
        <p style={{ margin: 0, color: "var(--color-text-muted)" }}>Nothing wrong with this hive right now.</p>
      )}

      {status === "ready" && open.map((issue) => (
        <div key={issue.issue_id}
             style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start",
                      padding: "var(--space-2) 0", borderBottom: "1px solid var(--color-border)" }}>
          <div style={{ flexGrow: 1 }}>
            <div>
              <Badge tone={issue.severity === "Urgent" ? "danger" : "warning"}>{issue.severity}</Badge>{" "}
              <strong>{issue.summary}</strong>
            </div>
            <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
              Since {formatDateOnly(issue.opened_date)}
              {issue.opened_by_name ? ` · found by ${issue.opened_by_name}` : ""}
            </div>
            {issue.notes && (
              <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>{issue.notes}</div>
            )}
          </div>
          <Button size="sm" variant="secondary" onClick={() => { setResolving(issue); setResolution(""); }}>
            Resolve
          </Button>
        </div>
      ))}

      {status === "ready" && resolved.length > 0 && (
        <details style={{ marginTop: "var(--space-3)" }}>
          <summary style={{ cursor: "pointer", color: "var(--color-text-muted)" }}>
            {resolved.length} resolved
          </summary>
          {resolved.map((issue) => (
            <div key={issue.issue_id}
                 style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start",
                          padding: "var(--space-2) 0", color: "var(--color-text-muted)" }}>
              <div style={{ flexGrow: 1 }}>
                <div>{issue.summary}</div>
                <div style={{ fontSize: "var(--font-size-sm)" }}>
                  Resolved {formatDateOnly(issue.resolved_date)}
                  {issue.resolved_by_name ? ` by ${issue.resolved_by_name}` : ""}
                  {issue.resolution ? ` — ${issue.resolution}` : ""}
                </div>
              </div>
              {/* An issue that comes back is the SAME issue. Opening a
                  second one loses the fact that it recurred, which is what
                  somebody reviewing a troublesome hive needs to see. */}
              <Button size="sm" variant="secondary" onClick={() => reopen(issue)}>Reopen</Button>
            </div>
          ))}
        </details>
      )}

      <Modal open={openForm} onClose={() => setOpenForm(false)} title="Log an issue"
        footer={<Button onClick={submit} disabled={saving}>{saving ? "Saving…" : "Log issue"}</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <Field label="What's wrong" required hint="A few words is plenty — 'queenless', 'wasps at the entrance'.">
            {(id, aria) => <input id={id} {...aria} className="touch-target" value={form.summary}
              onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} />}
          </Field>
          {/* Two options, not three. A middle one is where everything lands
              when the choice is hard, and a severity everything shares
              sorts nothing. */}
          <Field label="How bad">
            {(id, aria) => (
              <select id={id} {...aria} className="touch-target" value={form.severity}
                onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}>
                <option value="Watch">Keep an eye on it</option>
                <option value="Urgent">Needs dealing with now</option>
              </select>
            )}
          </Field>
          <Field label="Notes (optional)">
            {(id, aria) => <textarea id={id} {...aria} rows={2} value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />}
          </Field>
        </div>
      </Modal>

      <Modal open={!!resolving} onClose={() => setResolving(null)} title="Resolve this issue"
        footer={<Button onClick={confirmResolve} disabled={saving}>{saving ? "Saving…" : "Resolve"}</Button>}>
        <p style={{ marginTop: 0 }}>{resolving?.summary}</p>
        {/* Optional on purpose: most issues are resolved by simply doing
            the thing, and demanding an explanation is how a Resolve button
            stops being pressed. */}
        <Field label="What did you do? (optional)">
          {(id, aria) => <textarea id={id} {...aria} rows={2} value={resolution}
            onChange={(e) => setResolution(e.target.value)} />}
        </Field>
      </Modal>
    </Card>
  );
}
